/**
 * Knock Engine — Musical Intelligence Module
 * Scale-aware note selection, interval weighting, genre-driven patterns, Feel control.
 */

// ─── PART A: Scale-Aware Note Pools ───────────────────────────────────

const SCALE_PATTERNS = {
  minor:            [0, 2, 3, 5, 7, 8, 10],
  major:            [0, 2, 4, 5, 7, 9, 11],
  dorian:           [0, 2, 3, 5, 7, 9, 10],
  pentatonic_minor: [0, 3, 5, 7, 10],
};

const KEY_TO_MIDI = {
  'C': 36, 'C#': 37, 'Db': 37, 'D': 38, 'D#': 39, 'Eb': 39,
  'E': 40, 'F': 41, 'F#': 42, 'Gb': 42, 'G': 43, 'G#': 44,
  'Ab': 44, 'A': 45, 'A#': 46, 'Bb': 46, 'B': 47
};

export function getScaleNotes(key, scaleType) {
  const root = KEY_TO_MIDI[key] || 36;
  const pattern = SCALE_PATTERNS[scaleType] || SCALE_PATTERNS.minor;
  const notes = [];
  for (let octave = 0; octave < 2; octave++) {
    for (const interval of pattern) {
      notes.push(root + interval + (octave * 12));
    }
  }
  return notes;
}

export function midiToFrequency(midiNote) {
  return 440 * Math.pow(2, (midiNote - 69) / 12);
}

/**
 * Parse session key string (e.g., "F Minor", "C Major", "A") into root + scale type
 */
export function parseSessionKey(keyStr) {
  if (!keyStr) return { root: 'C', scaleType: 'minor' };
  const parts = keyStr.trim().split(/\s+/);
  const root = parts[0] || 'C';
  const qualifier = (parts[1] || '').toLowerCase();
  
  let scaleType = 'minor'; // default for bass
  if (qualifier === 'major' || qualifier === 'maj') scaleType = 'major';
  else if (qualifier === 'dorian' || qualifier === 'dor') scaleType = 'dorian';
  else if (qualifier.includes('pent')) scaleType = 'pentatonic_minor';
  
  return { root, scaleType };
}


// ─── PART B: Interval Weighting ──────────────────────────────────────

// Semitone intervals from root and their weights
const INTERVAL_WEIGHTS = [
  { interval: 0, weight: 35, name: 'root' },       // Root (unison)
  { interval: 7, weight: 25, name: 'fifth' },      // Perfect 5th
  { interval: 12, weight: 15, name: 'octave' },    // Octave
  { interval: 5, weight: 10, name: 'fourth' },     // Perfect 4th
  { interval: 3, weight: 5, name: 'minor3rd' },    // Minor 3rd
  { interval: 10, weight: 5, name: 'minor7th' },   // Minor 7th
];
// Remaining 5% split across other scale tones

/**
 * Weighted random selection from scale notes, biased toward strong intervals.
 */
export function pickWeightedNote(scaleNotes, rootMidi) {
  const r = Math.random() * 100;
  let cumulative = 0;
  
  for (const { interval, weight } of INTERVAL_WEIGHTS) {
    cumulative += weight;
    if (r < cumulative) {
      // Find the closest note in the scale pool to root + interval
      const target = rootMidi + interval;
      // Check both octaves
      const candidates = [target, target - 12, target + 12];
      for (const c of candidates) {
        if (scaleNotes.includes(c)) return c;
      }
      // Fallback: nearest scale note to target
      return findNearestScaleNote(scaleNotes, target);
    }
  }
  
  // 5% remaining — pick any other scale tone (passing tone)
  const nonRoot = scaleNotes.filter(n => (n - rootMidi) % 12 !== 0);
  return nonRoot.length > 0
    ? nonRoot[Math.floor(Math.random() * nonRoot.length)]
    : scaleNotes[0];
}

function findNearestScaleNote(scaleNotes, target) {
  let nearest = scaleNotes[0];
  let minDist = Math.abs(target - nearest);
  for (const n of scaleNotes) {
    const dist = Math.abs(target - n);
    if (dist < minDist) { nearest = n; minDist = dist; }
  }
  return nearest;
}


// ─── PART C: Feel Profiles ───────────────────────────────────────────

const FEEL_PROFILES = {
  basic: {
    density: 0.25,         // 25% of 64 steps = ~16 notes
    preferDownbeats: true, // Quarter notes on beats 1,2,3,4
    rootHeavy: true,       // Extra weight toward root
    velocityRange: [0.7, 1.0],
    allowChromatic: false,
    allowSlides: false,
  },
  bouncy: {
    density: 0.40,
    preferDownbeats: false, // Syncopated
    rootHeavy: false,       // More melodic movement
    velocityRange: [0.5, 0.9],
    allowChromatic: false,
    allowSlides: false,
  },
  sparse: {
    density: 0.15,
    preferDownbeats: true,
    rootHeavy: true,        // Root + fifth only
    velocityRange: [0.6, 0.8],
    allowChromatic: false,
    allowSlides: false,
    restrictToRootFifth: true, // Only root and 5th
  },
  busy: {
    density: 0.60,
    preferDownbeats: false,
    rootHeavy: false,
    velocityRange: [0.4, 1.0],
    allowChromatic: true,   // Chromatic passing tones allowed
    allowSlides: true,      // Pitch slides enabled
  },
};


// ─── PART D: Genre Overrides ─────────────────────────────────────────

const GENRE_OVERRIDES = {
  afrobeats: { swing: 0.15, octaveShift: 0, oscType: 'sine', filter: 300 },
  trap:      { swing: 0,    octaveShift: -12, oscType: 'sine', filter: 200, slides: true },
  'boom bap':{ swing: 0.08, octaveShift: 0, oscType: 'triangle', filter: 400 },
  'lo-fi':   { swing: 0,    octaveShift: 0, oscType: 'sine', filter: 200, detune: 8 },
  drill:     { swing: 0,    octaveShift: -12, oscType: 'sine', filter: 180, slides: true },
  dancehall: { swing: 0.12, octaveShift: 0, oscType: 'sine', filter: 350 },
};

/**
 * Detect genre from style tags array.
 */
export function detectGenre(styleTags) {
  if (!styleTags || styleTags.length === 0) return null;
  const lower = styleTags.map(t => t.toLowerCase());
  
  for (const genre of Object.keys(GENRE_OVERRIDES)) {
    if (lower.some(t => t.includes(genre))) return genre;
  }
  // Broader matching
  if (lower.some(t => t.includes('afro'))) return 'afrobeats';
  if (lower.some(t => t.includes('808') || t.includes('trap'))) return 'trap';
  if (lower.some(t => t.includes('boom') || t.includes('bap'))) return 'boom bap';
  if (lower.some(t => t.includes('lo-fi') || t.includes('lofi'))) return 'lo-fi';
  if (lower.some(t => t.includes('drill'))) return 'drill';
  return null;
}

export function getGenreOverrides(genre) {
  return genre ? (GENRE_OVERRIDES[genre] || null) : null;
}


// ─── PATTERN GENERATION ──────────────────────────────────────────────

/**
 * Generate a 4-bar bassline pattern (64 steps on 16th-note grid).
 * Returns: Array of { step, midiNote, frequency, duration, velocity, slide }
 */
export function generatePattern(key, scaleType, feel, genre, variationSeed = 0, kickPositions = []) {
  // Seed randomness for reproducibility
  let seed = variationSeed;
  const seededRandom = () => {
    seed = (seed * 1664525 + 1013904223) & 0xFFFFFFFF;
    return (seed >>> 0) / 0xFFFFFFFF;
  };
  // Use seeded random for initial generation, Math.random for variation
  const rng = variationSeed > 0 ? seededRandom : Math.random;
  
  const profile = FEEL_PROFILES[feel] || FEEL_PROFILES.basic;
  const genreOvr = getGenreOverrides(genre);
  const rootMidi = KEY_TO_MIDI[key] || 36;
  
  // Apply genre octave shift
  // Start in the sub-bass register. Trap/drill are already low; do not push them subsonic.
  const octaveShift = Math.min(-12, genreOvr?.octaveShift || 0);
  const effectiveRoot = rootMidi + octaveShift;
  const scaleNotes = getScaleNotes(key, scaleType).map(n => n + octaveShift);
  
  const totalSteps = 64; // 4 bars × 16 steps
  const noteCount = Math.round(totalSteps * profile.density);
  
  // Determine which steps get notes
  const activeSteps = new Set();
  
  if (profile.preferDownbeats) {
    // Place notes on downbeats first (steps 0, 4, 8, 12, 16, 20, ...)
    const downbeats = [];
    for (let i = 0; i < totalSteps; i += 4) downbeats.push(i);
    // Fill downbeats first
    for (const db of downbeats) {
      if (activeSteps.size >= noteCount) break;
      activeSteps.add(db);
    }
    // Fill remaining randomly
    while (activeSteps.size < noteCount) {
      const step = Math.floor(rng() * totalSteps);
      activeSteps.add(step);
    }
  } else {
    // Syncopated — prefer off-beats (steps 2, 6, 10, 14, ...)
    const offbeats = [];
    const onbeats = [];
    for (let i = 0; i < totalSteps; i++) {
      if (i % 4 === 2 || i % 4 === 3) offbeats.push(i);
      else onbeats.push(i);
    }
    // 60% off-beats, 40% on-beats
    const offCount = Math.round(noteCount * 0.6);
    const shuffled = [...offbeats].sort(() => rng() - 0.5);
    for (let i = 0; i < Math.min(offCount, shuffled.length); i++) {
      activeSteps.add(shuffled[i]);
    }
    const onShuffled = [...onbeats].sort(() => rng() - 0.5);
    let idx = 0;
    while (activeSteps.size < noteCount && idx < onShuffled.length) {
      activeSteps.add(onShuffled[idx++]);
    }
  }
  
  // Apply kick-aware positioning if available
  if (kickPositions.length > 0 && feel !== 'basic') {
    // Convert kick positions (0-1 in loop) to step positions
    const kickSteps = kickPositions.map(pos => Math.round(pos * totalSteps) % totalSteps);
    // Ensure notes land on or near kick positions
    for (const ks of kickSteps) {
      activeSteps.add(ks);
    }
  }
  
  // CRITICAL: Step 0 MUST always have a note — it is the "1", the downbeat anchor.
  // Only steps 1–63 are subject to density filtering.
  activeSteps.add(0);
  
  // Generate notes for each active step
  const pattern = [];
  const sortedSteps = [...activeSteps].sort((a, b) => a - b);
  
  for (const step of sortedSteps) {
    let midiNote;
    
    if (profile.restrictToRootFifth) {
      // Sparse: only root and fifth
      midiNote = rng() < 0.7 ? effectiveRoot : effectiveRoot + 7;
      // Ensure it's in scale
      if (!scaleNotes.includes(midiNote)) midiNote = effectiveRoot;
    } else if (profile.rootHeavy) {
      // Basic: extra root weight
      midiNote = rng() < 0.5 ? effectiveRoot : pickWeightedNote(scaleNotes, effectiveRoot);
    } else {
      // Bouncy/Busy: full weighted selection
      midiNote = pickWeightedNote(scaleNotes, effectiveRoot);
    }
    
    // The first hit establishes the low home note; later hits can add octave accents.
    if (step === 0) midiNote = effectiveRoot;

    // Calculate duration (in steps)
    let durationSteps = 1;
    if (profile.density <= 0.2) {
      // Sparse: longer notes (2-4 steps)
      durationSteps = Math.floor(rng() * 3) + 2;
    } else if (profile.density >= 0.5) {
      // Busy: short notes (1 step)
      durationSteps = 1;
    } else {
      // Normal: 1-2 steps
      durationSteps = rng() < 0.6 ? 1 : 2;
    }
    
    // Velocity
    const [vMin, vMax] = profile.velocityRange;
    const isDownbeat = step % 4 === 0;
    let velocity = vMin + rng() * (vMax - vMin);
    if (isDownbeat) velocity = Math.min(1.0, velocity + 0.15); // Accent downbeats
    
    // Slide flag (for busy/trap)
    const slide = false; // Slides are an intentional edit, never a random generation choice.
    
    pattern.push({
      step,
      midiNote,
      frequency: midiToFrequency(midiNote),
      durationSteps,
      velocity: Math.min(1.0, velocity),
      slide,
    });
  }
  
  return pattern;
}


// ─── PART E: Bass Synthesis ──────────────────────────────────────────

/**
 * Synthesize a bass note using OscillatorNode.
 * Connects through the provided gainNode for master volume control.
 */
export function createBassNote(ctx, gainNode, frequency, startTime, duration, options = {}) {
  const {
    oscType = 'sine',
    filterFreq = 300,
    detune = 0,
    slide = false,
    slideTarget = null,
    velocity = 0.8,
  } = options;
  
  const osc = ctx.createOscillator();
  const noteGain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  
  osc.type = oscType;
  osc.frequency.setValueAtTime(frequency, startTime);
  if (detune) osc.detune.setValueAtTime(detune, startTime);
  
  // Pitch slide (glide to next note)
  if (slide && slideTarget) {
    osc.frequency.linearRampToValueAtTime(slideTarget, startTime + duration * 0.8);
  }
  
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(filterFreq, startTime);
  filter.Q.value = 1;
  
  // Envelope: fast attack, sustain, exponential release
  noteGain.gain.setValueAtTime(0, startTime);
  noteGain.gain.linearRampToValueAtTime(velocity, startTime + 0.01);
  const releaseStart = startTime + duration - 0.02;
  if (releaseStart > startTime + 0.01) {
    noteGain.gain.setValueAtTime(velocity, releaseStart);
  }
  noteGain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  
  // Signal chain: osc → filter → noteGain → masterGain
  osc.connect(filter);
  filter.connect(noteGain);
  noteGain.connect(gainNode);
  
  osc.start(startTime);
  osc.stop(startTime + duration + 0.05);
  
  return osc;
}
